<template>
  <dialog-base
    v-bind="$props"
    :key="renderKey"
    :external-activator="true"
    :header="actionText"
    :loading="loading"
    :loading-message="loadingMessage"
    @close="close"
    @confirm="submit">
    <template #body="{ dialogActionsRef }">
      <form-base :teleport-target="dialogActionsRef">
        <template #form>
          <v-form ref="form" @keyup.enter="submit">
            <v-container class="px-7 pb-7">
              <v-text-field prepend-icon="mdi-email" disabled :model-value="email"></v-text-field>

              <text-field
                :required="true"
                :type="showPasswords ? 'text' : 'password'"
                prepend-icon="mdi-key"
                :append-icon="showPasswords ? 'mdi-eye' : 'mdi-eye-off'"
                v-model="password"
                :rules="$validator.rules.password"
                test-id="password-reset-password"
                @click:append="showPasswords = !showPasswords"
                :label="$t('auth.dialogs.passwordResetDialog.labels.password')"></text-field>

              <text-field
                :required="true"
                :type="showPasswords ? 'text' : 'password'"
                prepend-icon="mdi-key"
                :append-icon="showPasswords ? 'mdi-eye' : 'mdi-eye-off'"
                v-model="passwordConfirm"
                test-id="password-reset-password-confirm"
                :rules="
                  $validator.rules.eq(
                    password,
                    $t('auth.dialogs.passwordResetDialog.messages.passwordsDoNotMatch')
                  )
                "
                @click:append="showPasswords = !showPasswords"
                :label="$t('auth.dialogs.passwordResetDialog.labels.confirmPassword')"></text-field>

              <password-restrictions :password="password"></password-restrictions>
            </v-container>
          </v-form>
        </template>

        <template #form-actions>
          <action-group
            @cancel="close"
            confirm-icon="check"
            :confirm-label="actionText"
            @confirm="submit"></action-group>
        </template>
      </form-base>
    </template>
  </dialog-base>
</template>

<script>
import dialogMixin from '@satellite/components/mixins/dialogMixin';
/**
 * Button/Dialog for the password reset form
 * @displayName Password Reset Dialog
 */
export default {
  mixins: [dialogMixin],
  props: {
    /**
     * Email address of account
     */
    email: {
      type: String,
      required: false
    },
    /**
     * Password reset token
     */
    resetToken: {
      type: String,
      required: false
    },
    /**
     * If true it will set the isEmailVerified=true as well
     */
    verifyAccount: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      password: '',
      passwordConfirm: '',
      showPasswords: false,
      loading: false
    };
  },
  computed: {
    actionText() {
      return this.verifyAccount
        ? this.$t('auth.dialogs.passwordResetDialog.headers.createYourPassword')
        : this.$t('auth.dialogs.passwordResetDialog.headers.resetPassword');
    },
    loadingMessage() {
      return this.verifyAccount
        ? this.$t('auth.dialogs.passwordResetDialog.messages.settingPassword')
        : this.$t('auth.dialogs.passwordResetDialog.messages.resettingPassword');
    }
  },
  methods: {
    /**
     * Removes params since they are what trigger the password reset dialog
     */
    close() {
      this.$router.replace('/login');
    },
    /**
     * Submit the form
     * @public
     */
    async submit() {
      if (!(await this.$isFormValid(this.$refs.form))) {
        return;
      }
      this.loading = true;
      const action = this.verifyAccount ? 'verifyAndResetPassword' : 'resetPassword';

      this.$store
        .dispatch(`Auth/${action}`, {
          email: this.email,
          password: this.password,
          token: this.resetToken
        })
        .then(() => {
          const message = this.verifyAccount
            ? this.$t('auth.dialogs.passwordResetDialog.messages.accountVerifiedAndPasswordSet')
            : this.$t('auth.dialogs.passwordResetDialog.messages.passwordHasBeenReset');
          this.notify(message);
          this.$router.replace('/login?showLogin=true');
        })
        .finally(() => {
          this.loading = false;
        });
      if (this.verifyAccount) {
        this.mixpanel.track(this.mixpanel.events.ACCOUNT.CREATED_PASSWORD, {
          'User Invite Email': this.email
        });
      }
    }
  }
};
</script>
